import { useTranslationAuth } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
import { useEffect, useState } from 'react';

import { defaultApiWrapperState } from './default';
import { API_FETCH_ABORT_SYMBOL, API_FORBIDDEN_EXCEPTION_ERROR, API_GLOBAL_ERROR } from './symbols';
import {
	ApiWrapperError,
	ApiWrapperSetupOptions,
	ApiWrapperState,
	ICallbacks,
	ICommonApi,
	IComponentStatus,
	MakeRequestOptions,
	ServiceRequest,
	ServiceRequestOptions,
	WrapRequestOptions,
} from './types';

export { ApiWrapperError } from './types';

const common: ApiWrapperState = defaultApiWrapperState;
const componentStatus: Record<string, IComponentStatus> = {};

export const apiWrapperSetup = (options: ApiWrapperSetupOptions) => {
	common.commonLocalErrorHandler = options.commonLocalErrorHandler;
	common.commonGlobalErrorHandler = options.commonGlobalErrorHandler;
};

export interface WrapRequest<ArgumentType, ResultType> {
	isLoading: boolean;
	isError: boolean;
	fetch: (args: ArgumentType, options?: ServiceRequestOptions) => ICommonApi<ResultType>;
	response: ResultType | null;
	abortController: AbortController;
	maybeAbort: () => void;
	resetInstance: () => void;
}

/**
 * @deprecated Use `useQuery` or `useMutation` from @tanstack/react-query instead.
 */
export const useWrappedRequest = <ArgumentType = void, ResultType = void>(
	serviceMethod: ServiceRequest<ArgumentType, ResultType>,
	options: MakeRequestOptions = {},
	wrapRequestOptions: WrapRequestOptions<ResultType> = {}
): WrapRequest<ArgumentType, ResultType> => {
	const { onSuccessDefault, onAnyErrorDefault, onGlobalErrorDefault, onLocalErrorDefault } =
		wrapRequestOptions;
	const { disableDefaultLocalErrorHandler = false } = options;
	const [id] = useState(generateUUID());
	const [response, setResponse] = useState<ResultType | null>(null);
	const [isError, setIsError] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(false);
	const [abortController, setAbortController] = useState<AbortController>(new AbortController());
	const tAuth = useTranslationAuth();

	/**
	 * Maybe abort means that if request is in progress, then it will be aborted.
	 * If request is not in progress, then it will do nothing.
	 * Thats why there is "maybe" since it's not guaranteed that it will abort anything.
	 * @returns void
	 */
	const maybeAbort = () => {
		if (!abortController) {
			return;
		}
		abortController.abort('Maybe abort has been called');
	};

	// If component is being unmounted, stop all the requests
	useEffect(() => {
		componentStatus[id] = {
			alive: true,
		};
		return () => {
			componentStatus[id].alive = false;
		};
	}, []);

	const resetInstance = () => {
		setIsLoading(false);
		setIsError(false);
		setResponse(null);
		abortController.abort();
		setAbortController(new AbortController());
	};

	const fetch = (args: ArgumentType, options?: ServiceRequestOptions) => {
		const callbacks: ICallbacks<ResultType> = {};
		const promise = makeActualRequest(
			args,
			callbacks,
			{
				setIsLoading,
				setResponse,
				setIsError,
			},
			{ abortController: options?.abortController || abortController }
		);
		const commonApi: ICommonApi<ResultType> = {
			onSuccess: (callback) => {
				callbacks.onSuccess = callback;
				return commonApi;
			},
			onLocalError: (callback) => {
				callbacks.onLocalError = callback;
				return commonApi;
			},
			onGlobalError: (callback) => {
				callbacks.onGlobalError = callback;
				return commonApi;
			},
			onAnyError: (callback) => {
				callbacks.onAnyError = callback;
				return commonApi;
			},
			onComponentUnmountedAndRequestFinished: (callback) => {
				callbacks.onComponentUnmountedAndRequestFinished = callback;
				return commonApi;
			},
			onFinish: (callback) => {
				callbacks.onFinish = callback;
				return commonApi;
			},
			onUnsafeFinish: (callback) => {
				callbacks.onUnsafeFinish = callback;
				return commonApi;
			},
			onPromiseCancel: (callback) => {
				callbacks.onPromiseCancel = callback;
				return commonApi;
			},
			concludeWithPromise: () => promise,
		};

		return commonApi;
	};

	const makeActualRequest = async (
		args: Parameters<typeof fetch>[0],
		callbacks: ICallbacks<ResultType>,
		// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
		{ setIsLoading, setResponse }: Record<string, Function>,
		requestOptions: ServiceRequestOptions
	) => {
		/** d.slaby 15.11.2021
		 * During test phase I discovered that in Node env,
		 * we need to wait till next tick to get the callbacks
		 */
		setTimeout(() => {
			if (
				!common.commonLocalErrorHandler &&
				!callbacks.onLocalError &&
				!onLocalErrorDefault &&
				!callbacks.onAnyError
			) {
				throw new Error(
					`
                    Api Wrapper error occured but no hook for catching it were provided.
                    Please add onLocalError or onLocalErrorDefault or onAnyError or commonLocalErrorHandler.
                    Terminating request.
                    serviceMethod: ${serviceMethod.toString()}
                    args: ${JSON.stringify(args)}
                `
				);
			}
		});

		let isCanceled = false;
		setIsLoading(true);
		setIsError(false);
		try {
			const result = await serviceMethod(args, requestOptions);
			const cs = componentStatus[id];
			if (!cs.alive) {
				isCanceled = true;
				callbacks.onComponentUnmountedAndRequestFinished &&
					callbacks.onComponentUnmountedAndRequestFinished(result);
				return;
			}
			setResponse(result);
			setIsLoading(false);
			if (callbacks.onSuccess) {
				callbacks.onSuccess(result);
			} else if (onSuccessDefault) {
				onSuccessDefault(result);
			}
			return result;
		} catch (error) {
			const cs = componentStatus[id];
			if (!cs.alive) {
				isCanceled = true;
				callbacks.onComponentUnmountedAndRequestFinished &&
					callbacks.onComponentUnmountedAndRequestFinished(error);
				return;
			}
			setIsLoading(false);
			if (callbacks.onAnyError) {
				callbacks.onAnyError(error);
			} else if (onAnyErrorDefault) {
				onAnyErrorDefault(error);
			}
			if (
				error instanceof ApiWrapperError &&
				error?.symbol === API_FORBIDDEN_EXCEPTION_ERROR
			) {
				showToastV2({
					title: tAuth('permission.lack_of_authorization'),
					state: 'error',
				});

				return;
			}
			if (error instanceof ApiWrapperError && error?.symbol === API_GLOBAL_ERROR) {
				setIsError(true);
				if (!common.commonGlobalErrorHandler && !callbacks.onGlobalError) {
					throw new Error(
						`Global error has been thrown but no handler has been provided: ${error}`
					);
				}
				if (callbacks.onGlobalError) {
					callbacks.onGlobalError(error);
				} else if (onGlobalErrorDefault) {
					onGlobalErrorDefault(error);
				} else if (common.commonGlobalErrorHandler) {
					common.commonGlobalErrorHandler(error);
				}
			} else if (
				error instanceof ApiWrapperError &&
				error?.symbol === API_FETCH_ABORT_SYMBOL
			) {
				isCanceled = true;
				callbacks.onPromiseCancel && callbacks.onPromiseCancel(error);
			} else {
				setIsError(!!error);
				if (callbacks.onLocalError) {
					callbacks.onLocalError(error);
				} else if (onLocalErrorDefault) {
					onLocalErrorDefault(error);
				} else if (!disableDefaultLocalErrorHandler && common.commonLocalErrorHandler) {
					common.commonLocalErrorHandler(error);
				}
			}
		} finally {
			callbacks.onUnsafeFinish && callbacks.onUnsafeFinish();
			if (!isCanceled) {
				callbacks.onFinish && callbacks.onFinish();
			}
		}
		return undefined;
	};

	return {
		isLoading,
		isError,
		fetch,
		response,
		abortController,
		maybeAbort,
		resetInstance,
	};
};
